import React, { useEffect, useState } from 'react';
import AppButton from '@mysimpledb/shared/components/UI/Button/Button';
import { useVault } from '@mysimpledb/shared/context/VaultContext';
import './SensitiveVaultWeb.css';

function formatDate(value) {
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available';

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function formatAlgorithm(value) {
  if (!value) return 'Not available';

  return String(value)
    .replace(/^aes-256-gcm$/i, 'AES-256-GCM')
    .replace(/^pbkdf2-sha256$/i, 'PBKDF2-SHA-256')
    .replace(/^pbkdf2$/i, 'PBKDF2-SHA-256')
    .replace(/^argon2id$/i, 'Argon2id');
}

function VaultStateIcon({ unlocked }) {
  return unlocked ? (
    <div className="secov__icon" aria-hidden>
      🔓
    </div>
  ) : (
    <div className="secov__icon" aria-hidden>
      🔐
    </div>
  );
}

function DetailRow({ label, value }) {
  return (
    <div className="svault__detail-row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

export default function SensitiveVaultWeb() {
  const {
    isVaultUnlocked,
    vaultStatus,
    isVaultStatusLoading,
    vaultStatusError,
    refreshVaultStatus,
    changeVaultPassphrase,
    generateNewVaultRecoveryKey,
    unlockVault,
    unlockVaultWithRecoveryKey,
    lockVault,
    isVaultUnlocking,
    clearVaultError,
    setupVault,
    isVaultSettingUp,
  } = useVault();

  const [dialog, setDialog] = useState(null);
  const [unlockMethod, setUnlockMethod] = useState('passphrase');
  const [unlockCredential, setUnlockCredential] = useState('');
  const [newPassphrase, setNewPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [setupPassphrase, setSetupPassphrase] = useState('');
  const [confirmSetupPassphrase, setConfirmSetupPassphrase] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionMessage, setActionMessage] = useState('');
  const [isWorking, setIsWorking] = useState(false);
  const [recoveryKeySaved, setRecoveryKeySaved] = useState(false);
  const [recoveryKeyCopied, setRecoveryKeyCopied] = useState(false);
  const [recoveryKeySource, setRecoveryKeySource] = useState('regenerated');

  useEffect(() => {
    refreshVaultStatus().catch(() => {});
  }, [refreshVaultStatus]);

  useEffect(() => {
    if (!dialog) return undefined;

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;

      if (dialog === 'show-recovery-key' || isWorking) {
        event.preventDefault();
        return;
      }

      closeDialog();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [dialog, isWorking]);

  const configured = Boolean(vaultStatus?.configured);
  const enabled = Boolean(vaultStatus?.enabled);
  const passphraseWrapper = vaultStatus?.passphraseWrapper || null;
  const recoveryWrapper = vaultStatus?.recoveryWrapper || null;

  const closeDialog = (force = false) => {
    if (isWorking && !force) return;
    setDialog(null);
    setUnlockCredential('');
    setNewPassphrase('');
    setConfirmPassphrase('');
    setSetupPassphrase('');
    setConfirmSetupPassphrase('');
    setRecoveryKeySaved(false);
    setRecoveryKeyCopied(false);
    setActionError('');
  };

  const openDialog = (name) => {
    setActionError('');
    setActionMessage('');
    setUnlockCredential('');
    setNewPassphrase('');
    setConfirmPassphrase('');
    setSetupPassphrase('');
    setConfirmSetupPassphrase('');
    setRecoveryKeySaved(false);
    setRecoveryKeyCopied(false);
    setDialog(name);
  };

  const handleSetupVault = async (event) => {
    event.preventDefault();
    setActionError('');
    clearVaultError?.();

    if (setupPassphrase.length < 12) {
      setActionError('Use at least 12 characters for your vault passphrase.');
      return;
    }

    if (setupPassphrase !== confirmSetupPassphrase) {
      setActionError('The vault passphrase entries do not match.');
      return;
    }

    setIsWorking(true);

    try {
      const result = await setupVault({ passphrase: setupPassphrase });
      setRecoveryKey(result.recoveryKey);
      setRecoveryKeySource('setup');
      setRecoveryKeySaved(false);
      setRecoveryKeyCopied(false);
      setSetupPassphrase('');
      setConfirmSetupPassphrase('');
      setDialog('show-recovery-key');
      setActionMessage(
        'Your Sensitive Vault was created successfully. Save your recovery key now.'
      );
    } catch (err) {
      setActionError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to set up the Sensitive Vault.'
      );
    } finally {
      setIsWorking(false);
    }
  };

  const handleChangePassphrase = async (event) => {
    event.preventDefault();
    setActionError('');

    if (newPassphrase.length < 12) {
      setActionError('Use at least 12 characters for the new passphrase.');
      return;
    }

    if (newPassphrase !== confirmPassphrase) {
      setActionError('The new passphrase entries do not match.');
      return;
    }

    setIsWorking(true);
    try {
      await changeVaultPassphrase({ newPassphrase });
      setActionMessage(
        'Your Sensitive Vault passphrase was changed successfully.'
      );
      closeDialog(true);
    } catch (err) {
      setActionError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to change the vault passphrase.'
      );
    } finally {
      setIsWorking(false);
    }
  };

  const handleGenerateRecoveryKey = async (event) => {
    event.preventDefault();
    setActionError('');
    setIsWorking(true);

    try {
      const nextRecoveryKey = await generateNewVaultRecoveryKey();
      setRecoveryKey(nextRecoveryKey);
      setRecoveryKeySource('regenerated');
      setRecoveryKeySaved(false);
      setRecoveryKeyCopied(false);
      setUnlockCredential('');
      setDialog('show-recovery-key');
      setActionMessage(
        'Your previous recovery key has been permanently invalidated. Save the new recovery key now.'
      );
    } catch (err) {
      setActionError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to generate a new recovery key.'
      );
    } finally {
      setIsWorking(false);
    }
  };

  const handleOpenVault = async (event) => {
    event.preventDefault();
    setActionError('');
    clearVaultError?.();

    if (!unlockCredential.trim()) {
      setActionError(
        unlockMethod === 'recovery'
          ? 'Enter your recovery key.'
          : 'Enter your vault passphrase.'
      );
      return;
    }

    setIsWorking(true);
    try {
      if (unlockMethod === 'recovery') {
        await unlockVaultWithRecoveryKey({ recoveryKey: unlockCredential });
      } else {
        await unlockVault({ passphrase: unlockCredential });
      }

      setUnlockCredential('');
      setActionMessage('Your Sensitive Vault is now open.');
      setDialog(null);
      await refreshVaultStatus().catch(() => {});
    } catch (err) {
      setActionError(
        err?.response?.data?.error ||
          err?.message ||
          'Failed to open the Sensitive Vault.'
      );
    } finally {
      setIsWorking(false);
    }
  };

  const handleLockVault = () => {
    lockVault();
    setActionError('');
    setActionMessage('Your Sensitive Vault is now locked.');
  };

  const copyRecoveryKey = async () => {
    setActionError('');

    try {
      await navigator.clipboard.writeText(recoveryKey);
      setRecoveryKeyCopied(true);
      setActionMessage('Recovery key copied to the clipboard.');
    } catch {
      setRecoveryKeyCopied(false);
      setActionError(
        'Could not copy automatically. Select and copy the key manually.'
      );
    }
  };

  const printRecoveryKey = () => {
    const generatedAt = formatDate(new Date().toISOString());
    const printWindow = window.open('', '_blank', 'width=720,height=760');

    if (!printWindow) {
      setActionError(
        'Your browser blocked the print window. Allow pop-ups and try again.'
      );
      return;
    }

    printWindow.opener = null;

    const escapedKey = recoveryKey
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');

    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>MySimpleDB Sensitive Vault Recovery Key</title>
          <style>
            body {
              margin: 0;
              padding: 48px;
              color: #111827;
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
            }
            .card {
              max-width: 680px;
              margin: 0 auto;
              padding: 32px;
              border: 2px solid #cbd5e1;
              border-radius: 16px;
            }
            h1 {
              margin: 0 0 8px;
              font-size: 26px;
            }
            h2 {
              margin: 28px 0 12px;
              font-size: 18px;
            }
            .key {
              padding: 18px;
              border: 1px solid #94a3b8;
              border-radius: 10px;
              background: #f8fafc;
              font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
              font-size: 18px;
              font-weight: 700;
              line-height: 1.6;
              overflow-wrap: anywhere;
            }
            .meta,
            .note {
              color: #475569;
              line-height: 1.5;
            }
            .note {
              margin-top: 24px;
              padding-top: 20px;
              border-top: 1px solid #e2e8f0;
            }
            @media print {
              body {
                padding: 0;
              }
              .card {
                border-color: #111827;
              }
            }
          </style>
        </head>
        <body>
          <main class="card">
            <h1>MySimpleDB Sensitive Vault</h1>
            <p class="meta">Recovery key generated ${generatedAt}</p>
            <h2>Recovery Key</h2>
            <div class="key">${escapedKey}</div>
            <p class="note">
              Store this recovery key in a secure location. It can unlock your
              Sensitive Vault if you forget your passphrase. MySimpleDB cannot
              display this key again.
            </p>
          </main>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const finishRecoveryKeyDisplay = () => {
    if (!recoveryKeySaved) return;

    setRecoveryKey('');
    setActionError('');
    setActionMessage(
      recoveryKeySource === 'setup'
        ? 'Your Sensitive Vault is configured and ready to use.'
        : 'Your new recovery key is active and has been saved.'
    );
    setDialog(null);
  };

  return (
    <section className="svault">
      <header className="svault__header">
        <div>
          <h2 className="svault__title">Sensitive Vault</h2>
          <p className="svault__lead">
            Review the encryption configuration and current status of your
            sensitive data vault.
          </p>
        </div>

        <div
          className={`svault__state-badge ${
            isVaultUnlocked ? 'svault__state-badge--unlocked' : ''
          }`}
        >
          <VaultStateIcon unlocked={isVaultUnlocked} />
          {isVaultUnlocked ? 'Unlocked' : 'Locked'}
        </div>
      </header>

      {actionMessage ? (
        <div className="svault__message svault__message--success" role="status">
          {actionMessage}
        </div>
      ) : null}

      {vaultStatusError ? (
        <div className="svault__message svault__message--error" role="alert">
          {vaultStatusError}
          <button type="button" onClick={refreshVaultStatus}>
            Try again
          </button>
        </div>
      ) : null}

      {isVaultStatusLoading && !vaultStatus ? (
        <div className="svault__message">Loading vault details...</div>
      ) : null}

      {!isVaultStatusLoading && vaultStatus && !configured ? (
        <div className="svault__setup-layout">
          <article className="svault__setup-card svault__setup-card--primary">
            <div className="svault__setup-icon" aria-hidden>
              🔐
            </div>
            <div>
              <span className="svault__eyebrow">Optional setup</span>
              <h3>Your Sensitive Vault has not been set up yet</h3>
              <p>
                The first time you save a Sensitive entry, MySimpleDB can guide
                you through creating a vault passphrase and recovery key.
              </p>
              <p>
                You can also set up the vault now so it is ready before you add
                sensitive information.
              </p>
              <AppButton
                variant="solid"
                onClick={() => openDialog('setup-vault')}
                disabled={isWorking || isVaultSettingUp}
              >
                Set Up Sensitive Vault
              </AppButton>
            </div>
          </article>

          <article className="svault__setup-card">
            <h3>What is the Sensitive Vault?</h3>
            <p>
              Sensitive entry details are encrypted on your device before they
              are stored. Your vault passphrase protects the encryption key and
              is not sent to MySimpleDB as readable vault data.
            </p>
            <div className="svault__example-grid">
              <div>
                <strong>Useful for</strong>
                <span>Passwords, banking details, recovery codes, keys, and private notes</span>
              </div>
              <div>
                <strong>Recovery</strong>
                <span>
                  A one-time recovery key is created during setup in case you
                  forget your vault passphrase.
                </span>
              </div>
            </div>
          </article>
        </div>
      ) : null}

      {configured ? (
        <>
          <div className="svault__summary-grid">
            <article className="svault__summary-card">
              <span className="svault__summary-label">Status</span>
              <strong>{enabled ? 'Configured and enabled' : 'Disabled'}</strong>
              <span>
                {isVaultUnlocked
                  ? 'Sensitive entries are available in this session.'
                  : 'Unlock the vault to view sensitive entries.'}
              </span>
            </article>

            <article className="svault__summary-card">
              <span className="svault__summary-label">Sensitive entries</span>
              <strong>{vaultStatus?.sensitiveEntryCount ?? 0}</strong>
              <span>Entries currently marked Sensitive</span>
            </article>

            <article className="svault__summary-card">
              <span className="svault__summary-label">
                Sensitive attachments
              </span>
              <strong>{vaultStatus?.sensitiveAttachmentCount ?? 0}</strong>
              <span>Attachments belonging to Sensitive entries</span>
            </article>
          </div>

          <div className="svault__content-grid">
            <article className="svault__card">
              <h3>Encryption</h3>
              <dl className="svault__details">
                <DetailRow
                  label="Vault version"
                  value={vaultStatus?.vaultVersion ?? 'Not available'}
                />
                <DetailRow
                  label="Encryption algorithm"
                  value={formatAlgorithm(vaultStatus?.vaultAlgorithm)}
                />
                <DetailRow
                  label="Key length"
                  value={
                    vaultStatus?.keyLengthBits
                      ? `${vaultStatus.keyLengthBits} bits`
                      : 'Not available'
                  }
                />
                <DetailRow
                  label="Key version"
                  value={vaultStatus?.keyVersion ?? 'Not available'}
                />
              </dl>
            </article>

            <article className="svault__card">
              <h3>Key derivation</h3>
              <dl className="svault__details">
                <DetailRow
                  label="Algorithm"
                  value={formatAlgorithm(passphraseWrapper?.kdfAlgorithm)}
                />
                <DetailRow
                  label="Hash"
                  value={vaultStatus?.kdfHash || 'SHA-256'}
                />
                <DetailRow
                  label="Iterations"
                  value={
                    passphraseWrapper?.iterations
                      ? Number(passphraseWrapper.iterations).toLocaleString()
                      : 'Not available'
                  }
                />
                <DetailRow
                  label="Wrapper algorithm"
                  value={formatAlgorithm(passphraseWrapper?.wrapAlgorithm)}
                />
              </dl>
            </article>

            <article className="svault__card">
              <h3>History</h3>
              <dl className="svault__details">
                <DetailRow
                  label="Vault created"
                  value={formatDate(vaultStatus?.createdAt)}
                />
                <DetailRow
                  label="Passphrase wrapper updated"
                  value={formatDate(passphraseWrapper?.updatedAt)}
                />
                <DetailRow
                  label="Recovery key generated"
                  value={formatDate(recoveryWrapper?.updatedAt)}
                />
                <DetailRow
                  label="Last unlocked"
                  value={formatDate(vaultStatus?.lastUnlockedAt)}
                />
              </dl>
            </article>

            <article className="svault__card svault__card--actions">
              <h3>Vault management</h3>
              <p>
                Change the passphrase that protects your vault key or replace
                your recovery key. Sensitive entries do not need to be
                re-encrypted when either wrapper changes.
              </p>
              {!isVaultUnlocked ? (
                <div className="svault__action-note">
                  Unlock the vault before using these management tools.
                </div>
              ) : null}
              <div className="svault__actions">
                <AppButton
                  variant={isVaultUnlocked ? 'outline' : 'solid'}
                  onClick={
                    isVaultUnlocked
                      ? handleLockVault
                      : () => openDialog('open-vault')
                  }
                >
                  {isVaultUnlocked ? 'Lock Vault' : 'Open Vault'}
                </AppButton>
                <AppButton
                  variant="solid"
                  disabled={!isVaultUnlocked}
                  onClick={() => openDialog('change-passphrase')}
                >
                  Change Passphrase
                </AppButton>
                <AppButton
                  variant="outline"
                  disabled={!isVaultUnlocked}
                  onClick={() => openDialog('generate-recovery-key')}
                >
                  Generate New Recovery Key
                </AppButton>
              </div>
            </article>
          </div>
        </>
      ) : null}

      {dialog ? (
        <div
          className="svault__modal-backdrop"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target !== event.currentTarget) return;
            if (dialog === 'show-recovery-key' || isWorking) return;
            closeDialog();
          }}
        >
          <div
            className="svault__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="svault-dialog-title"
          >
            {dialog === 'setup-vault' ? (
              <form onSubmit={handleSetupVault}>
                <h3 id="svault-dialog-title">Set Up Sensitive Vault</h3>
                <p>
                  Create a separate passphrase for your Sensitive Vault. You will
                  use it to unlock encrypted Sensitive entries.
                </p>
                <div className="svault__warning">
                  <strong>Do not lose this passphrase.</strong>
                  <span>
                    MySimpleDB cannot recover it. You will receive a recovery key
                    after setup as an alternate way to unlock the vault.
                  </span>
                </div>
                <label>
                  Vault passphrase
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={setupPassphrase}
                    onChange={(event) => setSetupPassphrase(event.target.value)}
                    minLength={12}
                    required
                    autoFocus
                  />
                </label>
                <label>
                  Confirm vault passphrase
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmSetupPassphrase}
                    onChange={(event) =>
                      setConfirmSetupPassphrase(event.target.value)
                    }
                    minLength={12}
                    required
                  />
                </label>
                <p className="svault__field-help">
                  Use at least 12 characters. This may be different from your
                  MySimpleDB account password.
                </p>
                {actionError ? (
                  <div className="svault__form-error">{actionError}</div>
                ) : null}
                <div className="svault__modal-actions">
                  <button
                    type="button"
                    className="svault__button svault__button--secondary"
                    onClick={closeDialog}
                    disabled={isWorking || isVaultSettingUp}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="svault__button svault__button--primary"
                    disabled={isWorking || isVaultSettingUp}
                  >
                    {isWorking || isVaultSettingUp
                      ? 'Setting Up…'
                      : 'Create Sensitive Vault'}
                  </button>
                </div>
              </form>
            ) : null}

            {dialog === 'open-vault' ? (
              <form onSubmit={handleOpenVault}>
                <h3 id="svault-dialog-title">Open Sensitive Vault</h3>
                <p>
                  Open the vault with your passphrase or recovery key. The vault
                  will remain open until you lock it or the session auto-locks.
                </p>

                <div
                  className="svault__unlock-tabs"
                  role="tablist"
                  aria-label="Vault unlock method"
                >
                  <button
                    type="button"
                    className={`svault__unlock-tab ${
                      unlockMethod === 'passphrase'
                        ? 'svault__unlock-tab--active'
                        : ''
                    }`}
                    onClick={() => {
                      setUnlockMethod('passphrase');
                      setUnlockCredential('');
                      setActionError('');
                    }}
                  >
                    Passphrase
                  </button>
                  <button
                    type="button"
                    className={`svault__unlock-tab ${
                      unlockMethod === 'recovery'
                        ? 'svault__unlock-tab--active'
                        : ''
                    }`}
                    onClick={() => {
                      setUnlockMethod('recovery');
                      setUnlockCredential('');
                      setActionError('');
                    }}
                  >
                    Recovery Key
                  </button>
                </div>

                <label>
                  {unlockMethod === 'recovery'
                    ? 'Recovery key'
                    : 'Vault passphrase'}
                  <input
                    type="password"
                    autoComplete={
                      unlockMethod === 'recovery' ? 'off' : 'current-password'
                    }
                    value={unlockCredential}
                    onChange={(event) =>
                      setUnlockCredential(event.target.value)
                    }
                    required
                    autoFocus
                  />
                </label>

                {actionError ? (
                  <div className="svault__form-error">{actionError}</div>
                ) : null}

                <div className="svault__modal-actions">
                  <button
                    type="button"
                    className="svault__button svault__button--secondary"
                    onClick={closeDialog}
                    disabled={isWorking || isVaultUnlocking}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="svault__button svault__button--primary"
                    disabled={isWorking || isVaultUnlocking}
                  >
                    {isWorking || isVaultUnlocking ? 'Opening…' : 'Open Vault'}
                  </button>
                </div>
              </form>
            ) : null}

            {dialog === 'change-passphrase' ? (
              <form onSubmit={handleChangePassphrase}>
                <h3 id="svault-dialog-title">Change Vault Passphrase</h3>
                <p>
                  This replaces only the passphrase wrapper around your vault
                  key. Your Sensitive entries remain encrypted with the same
                  vault key.
                </p>
                <label>
                  New passphrase
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={newPassphrase}
                    onChange={(event) => setNewPassphrase(event.target.value)}
                    minLength={12}
                    required
                    autoFocus
                  />
                </label>
                <label>
                  Confirm new passphrase
                  <input
                    type="password"
                    autoComplete="new-password"
                    value={confirmPassphrase}
                    onChange={(event) =>
                      setConfirmPassphrase(event.target.value)
                    }
                    minLength={12}
                    required
                  />
                </label>
                {actionError ? (
                  <div className="svault__form-error">{actionError}</div>
                ) : null}
                <div className="svault__modal-actions">
                  <button
                    type="button"
                    className="svault__button svault__button--secondary"
                    onClick={closeDialog}
                    disabled={isWorking}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="svault__button svault__button--primary"
                    disabled={isWorking}
                  >
                    {isWorking ? 'Changing…' : 'Change Passphrase'}
                  </button>
                </div>
              </form>
            ) : null}

            {dialog === 'generate-recovery-key' ? (
              <form onSubmit={handleGenerateRecoveryKey}>
                <h3 id="svault-dialog-title">Generate New Recovery Key</h3>
                <div className="svault__warning" role="alert">
                  <strong>
                    This permanently invalidates your existing recovery key.
                  </strong>
                  <span>
                    Anyone holding the current recovery key will no longer be
                    able to use it after the replacement succeeds.
                  </span>
                </div>
                <ul className="svault__impact-list">
                  <li>Your vault data will not be re-encrypted.</li>
                  <li>Your vault passphrase will not change.</li>
                  <li>Only the recovery-key wrapper will be replaced.</li>
                </ul>
                <p>
                  The current key remains valid unless the new wrapper is saved
                  successfully.
                </p>
                {actionError ? (
                  <div className="svault__form-error">{actionError}</div>
                ) : null}
                <div className="svault__modal-actions">
                  <button
                    type="button"
                    className="svault__button svault__button--secondary"
                    onClick={closeDialog}
                    disabled={isWorking}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="svault__button svault__button--danger"
                    disabled={isWorking}
                  >
                    {isWorking ? 'Generating…' : 'Generate New Recovery Key'}
                  </button>
                </div>
              </form>
            ) : null}

            {dialog === 'show-recovery-key' ? (
              <>
                <h3 id="svault-dialog-title">
                  {recoveryKeySource === 'setup'
                    ? 'Save Your Recovery Key'
                    : 'Save Your New Recovery Key'}
                </h3>
                <p>
                  {recoveryKeySource === 'setup'
                    ? 'Your Sensitive Vault is now configured and unlocked. This recovery key can unlock it if you forget your vault passphrase.'
                    : 'Your previous recovery key has been permanently invalidated. This new key is now the only recovery key that can unlock your Sensitive Vault if you forget your passphrase.'}
                </p>
                <div className="svault__warning" role="alert">
                  <strong>This key will not be shown again.</strong>
                  <span>
                    Copy, print, or securely record it before continuing.
                  </span>
                </div>
                <div
                  className="svault__recovery-key"
                  tabIndex="0"
                  aria-label="New Sensitive Vault recovery key"
                >
                  {recoveryKey}
                </div>
                {recoveryKeyCopied ? (
                  <div className="svault__copy-status" role="status">
                    ✓ Recovery key copied to clipboard.
                  </div>
                ) : null}
                {actionError ? (
                  <div className="svault__form-error">{actionError}</div>
                ) : null}
                <label className="svault__saved-confirmation">
                  <input
                    type="checkbox"
                    checked={recoveryKeySaved}
                    onChange={(event) =>
                      setRecoveryKeySaved(event.target.checked)
                    }
                  />
                  <span>
                    I have saved this recovery key in a secure location.
                  </span>
                </label>
                <div className="svault__modal-actions svault__modal-actions--spread">
                  <div className="svault__recovery-tools">
                    <button
                      type="button"
                      className="svault__button svault__button--secondary"
                      onClick={copyRecoveryKey}
                    >
                      Copy Key
                    </button>
                    <button
                      type="button"
                      className="svault__button svault__button--secondary"
                      onClick={printRecoveryKey}
                    >
                      Print
                    </button>
                  </div>
                  <button
                    type="button"
                    className="svault__button svault__button--primary"
                    onClick={finishRecoveryKeyDisplay}
                    disabled={!recoveryKeySaved}
                  >
                    I&apos;ve Saved It
                  </button>
                </div>
              </>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
